import { take } from 'rxjs/operators';
import { SupplierService } from '@tcc-mono/vandijk/shared/data-access/vandijk-api';
import { withPagination, selectCurrentPageEntities, selectPaginationData, selectCurrentPage, updatePaginationData, setPage, skipWhilePageExists, setCurrentPage, getPaginationData } from '@ngneat/elf-pagination';
import { Supplier } from '@tcc-mono/vandijk/shared/data-access/models';
import { withEntities, selectActiveEntity, withActiveId, upsertEntities, setActiveId, hasEntity, getActiveId, deleteEntities } from '@ngneat/elf-entities';
import { createStore } from '@ngneat/elf';
import { inject, Injectable } from '@angular/core';
import { createRequestDataSource, withRequestsStatus } from '@ngneat/elf-requests';
import { EMPTY, map, tap, identity, Observable } from 'rxjs';
import { ElfUtil } from '@tcc-mono/shared/utils';
import { PaginatedResponse, Response } from '@tcc-mono/shared/data-access';


const store = createStore(
  { name: 'suppliers' },
  withEntities<Supplier>(),
  withPagination(),
  withActiveId(),
  withRequestsStatus()
);

const suppliersDataSource = createRequestDataSource({
  data$: () => store.pipe(selectCurrentPageEntities()),
  requestKey: 'suppliers',
  dataKey: 'suppliers',
  store
});

@Injectable({
  providedIn: 'root'
})
export class SuppliersRepository {
  private readonly _supplierService: SupplierService = inject(SupplierService);

  public suppliers$ = store.pipe(
    selectCurrentPageEntities()
  );

  public paginationData$ = store.pipe(
    selectPaginationData()
  );

  public currentPage$ = store.pipe(
    selectCurrentPage()
  );

  public activeSupplier$ = store.pipe(
    selectActiveEntity()
  );

  public loading$ = suppliersDataSource.data$().pipe(map(({ loading }) => loading));

  public setActiveSupplier = (id: number) => {
    if (store.query(getActiveId) !== id) {
      store.update(
        setActiveId(id)
      );

      if (!store.query(hasEntity(id))) {
        this.getSupplier(id)
          .pipe(take(1))
          .subscribe();
      }

    }
  }

  public setPage = (page: number, refreshPage = false) => {
    store.update(
      setCurrentPage(page)
    );

    this._getSuppliersByPage(page, refreshPage)
      .pipe(take(1))
      .subscribe();
  }

  private _getSuppliersByPage = (page: number | string, refreshPage = false) => {
    return this._supplierService.getAll({ page })
      .pipe(
        tap(this._upsertSuppliers),
        suppliersDataSource.trackRequestStatus(),
        refreshPage ? identity : skipWhilePageExists(store, page)
      );
  }

  public getSupplier = (id: number) => {
    const call = this._supplierService.get(id)
      .pipe(
        map(({ data }) => data),
        tap((supplier: Supplier) => store.update(upsertEntities(supplier)))
      );

    return store.query(hasEntity(id)) ? EMPTY : call;
  }

  private _upsertSuppliers = ({ data, meta }: PaginatedResponse<Supplier>) => {
    const pagination = ElfUtil.convertMetaToElfPagination(meta);

    store.update(
      upsertEntities(data),
      updatePaginationData(pagination),
      setPage(
        pagination.currentPage,
        data.map(c => c.id)
      ),
      suppliersDataSource.setSuccess()
    );
  }

  public addSupplier = (supplier: Supplier): Observable<Supplier> => {
    return this._supplierService.post(supplier)
      .pipe(
        map(({ data }) => data as Supplier),
        tap((newSupplier) => {
          store.update(
            upsertEntities(newSupplier)
          );

          const { currentPage } = store.query(getPaginationData());

          this.setPage(
            currentPage,
            true
          );
        })
      );
  }

  public updateSupplier = (supplier: Supplier): Observable<Supplier> => {
    return this._supplierService.put(supplier.id, supplier)
      .pipe(
        map(({ data }) => data as Supplier),
        tap((updatedSupplier) => {
          store.update(
            upsertEntities(updatedSupplier)
          );
        })
      );
  }

  public removeSupplier = (supplierId: string | number) => {
    return this._supplierService.delete(supplierId)
      .pipe(
        tap(() => {
          store.update(
            deleteEntities(+supplierId)
          );

          const { currentPage } = store.query(getPaginationData());

          this.setPage(
            currentPage,
            true
          );
        })
      )
  }

}
